/**
 * Shared Parameter Options Hook
 *
 * Provides shared parameter options for dropdown menus throughout the app.
 * Automatically loads parameters from the configured source (default or custom file).
 */

import { useEffect, useMemo, useCallback } from 'react';
import {
  useSharedParameterStore,
  useParameterOptions,
  useGroupedParameterOptions,
  useSharedParameterIsParsed,
  useSharedParameterLoading,
  useSharedParameterError,
} from '../stores/useSharedParameterStore';
import type { ParameterOption } from '../types/sharedParameter.types';

/**
 * Main hook for accessing shared parameter options in dropdowns
 */
export function useSharedParameterOptions() {
  const isParsed = useSharedParameterIsParsed();
  const isLoading = useSharedParameterLoading();
  const error = useSharedParameterError();
  const parameterOptions = useParameterOptions();
  const groupedOptions = useGroupedParameterOptions();

  const loadDefaultParameters = useSharedParameterStore(
    (state) => state.loadDefaultParameters
  );
  const loadFileContent = useSharedParameterStore(
    (state) => state.loadFileContent
  );
  const useDefaultFile = useSharedParameterStore(
    (state) => state.useDefaultFile
  );

  // Load default parameters on mount if not already parsed
  useEffect(() => {
    if (!isParsed && !isLoading && useDefaultFile) {
      loadDefaultParameters();
    }
  }, [isParsed, isLoading, useDefaultFile, loadDefaultParameters]);

  return {
    options: parameterOptions,
    groupedOptions,
    isLoading,
    error,
    isParsed,
    loadFileContent,
    loadDefaultParameters,
  };
}

/**
 * Hook to get parameters filtered by group name
 */
export function useParametersByGroup(groupName: string): ParameterOption[] {
  const { groupedOptions, isParsed } = useSharedParameterOptions();

  return useMemo(() => {
    if (!isParsed) return [];
    const group = groupedOptions.find((g) => g.groupName === groupName);
    return group?.options || [];
  }, [groupedOptions, groupName, isParsed]);
}

/**
 * Hook to get connector-related parameters
 */
export function useConnectorParameters(): ParameterOption[] {
  return useParametersByGroup('PiX_PPG_CONNECTORS');
}

/**
 * Hook to get MTO (Material Take-Off) parameters
 */
export function useMTOParameters(): ParameterOption[] {
  return useParametersByGroup('PiX_MTO');
}

/**
 * Hook to get general parameters
 */
export function useGeneralParameters(): ParameterOption[] {
  return useParametersByGroup('Pix_GEN');
}

/**
 * Hook to get PCF-related parameters
 */
export function usePCFParameters(): ParameterOption[] {
  return useParametersByGroup('#PCF');
}

/**
 * Hook to get bolt set parameters
 */
export function useBoltParameters(): ParameterOption[] {
  return useParametersByGroup('PiX_PPG_BoltSet');
}

/**
 * Hook to get geometry parameters
 */
export function useGeometryParameters(): ParameterOption[] {
  return useParametersByGroup('PiX_PPG_GEOMETRY');
}

/**
 * Hook to get property parameters
 */
export function usePropertyParameters(): ParameterOption[] {
  return useParametersByGroup('PiX_PPG_PROPERTIES');
}

/**
 * Hook to get titleblock parameters
 */
export function useTitleblockParameters(): ParameterOption[] {
  return useParametersByGroup('PiX_GEN_Titleblock');
}

/**
 * Hook to get system conditions parameters
 */
export function useSystemConditionsParameters(): ParameterOption[] {
  return useParametersByGroup('PiX_PPG_SystemConditions');
}

/**
 * Hook to get component parameters
 */
export function useComponentParameters(): ParameterOption[] {
  return useParametersByGroup('PiX_PPG_Components');
}

/**
 * Hook to search parameters by name
 */
export function useParameterSearch() {
  const { options, isParsed } = useSharedParameterOptions();

  const searchParameters = useCallback(
    (query: string): ParameterOption[] => {
      if (!isParsed || !query.trim()) return options;

      const lowerQuery = query.toLowerCase();
      return options.filter(
        (opt) =>
          opt.label.toLowerCase().includes(lowerQuery) ||
          opt.description?.toLowerCase().includes(lowerQuery)
      );
    },
    [options, isParsed]
  );

  return { searchParameters, allOptions: options };
}

/**
 * Converts parameter options to format expected by FormSelect component
 */
export function toFormSelectOptions(
  options: ParameterOption[]
): Array<{ value: string; label: string; tooltip?: string }> {
  return options.map((opt) => ({
    value: opt.value,
    label: opt.label,
    tooltip: opt.description,
  }));
}

/**
 * Gets combined options from multiple groups
 */
export function useCombinedGroupOptions(groupNames: string[]): ParameterOption[] {
  const { groupedOptions, isParsed } = useSharedParameterOptions();

  return useMemo(() => {
    if (!isParsed) return [];

    const combined: ParameterOption[] = [];
    for (const groupName of groupNames) {
      const group = groupedOptions.find((g) => g.groupName === groupName);
      if (group) {
        combined.push(...group.options);
      }
    }
    return combined.sort((a, b) => a.label.localeCompare(b.label));
  }, [groupedOptions, groupNames, isParsed]);
}
